import { apiClient } from './client';
import type { WorkflowNode, WorkflowEdge } from '@pm-next/shared-types';

/** API response: single workflow (Prisma snake_case) */
export interface ApiWorkflow {
  id: string;
  employer_id: number;
  name: string;
  description: string | null;
  module: string;
  status: string;
  version: number;
  graph_data: string | null;
  trigger_config: string | null;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

/** API response: list workflows */
export interface ApiWorkflowListResponse {
  data: ApiWorkflow[];
  total: number;
}

/** API response: workflow graph */
export interface ApiWorkflowGraphResponse {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  triggerConfig?: Record<string, unknown>;
}

/** API response: workflow template meta */
export interface ApiWorkflowTemplateMeta {
  id: string;
  name: string;
  description: string;
  module: string;
}

/** API response: workflow run with step logs */
export interface ApiWorkflowStepLog {
  id: string;
  workflow_run_id: string;
  step_id: string;
  step_type: string;
  step_label: string;
  status: string;
  input_snapshot: string | null;
  output_snapshot: string | null;
  message: string | null;
  executed_at: string;
  duration_ms: number | null;
}

export interface ApiWorkflowRun {
  id: string;
  workflow_id: string;
  workflow_version: number;
  employer_id: number;
  conversation_id: number;
  trigger_source: string;
  trigger_data: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  summary: string | null;
  is_test_run: boolean;
  created_at: string;
  step_logs?: ApiWorkflowStepLog[];
}

/** API response: list runs */
export interface ApiWorkflowRunsResponse {
  data: ApiWorkflowRun[];
  total: number;
}

/** API response: workflow-generated notification */
export interface ApiNotification {
  id: string;
  employer_id: number;
  workflow_run_id: string | null;
  title: string;
  message: string | null;
  is_read: boolean;
  created_at: string;
}

export interface ApiNotificationsResponse {
  data: ApiNotification[];
  total: number;
}

/** API response: workflow-generated task */
export interface ApiTask {
  id: string;
  employer_id: number;
  workflow_run_id: string | null;
  title: string;
  message: string | null;
  status: string;
  created_at: string;
}

export interface ApiTasksResponse {
  data: ApiTask[];
  total: number;
}

export interface ListParams {
  limit?: number;
  offset?: number;
}

export interface CreateWorkflowBody {
  name: string;
  description?: string;
  module?: string;
}

export interface UpdateWorkflowBody {
  name?: string;
  description?: string;
  module?: string;
}

export interface ListWorkflowsParams {
  status?: string;
  module?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface SaveWorkflowGraphBody {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  triggerConfig?: Record<string, unknown>;
}

export interface TestWorkflowBody {
  triggerData?: Record<string, unknown>;
}

export interface ListWorkflowRunsParams {
  limit?: number;
  offset?: number;
}

// Workflow CRUD
export async function createWorkflow(data: CreateWorkflowBody): Promise<ApiWorkflow> {
  const res = await apiClient.post<ApiWorkflow>('/workflows', data);
  return res.data;
}

export async function listWorkflows(
  params?: ListWorkflowsParams
): Promise<ApiWorkflowListResponse> {
  const res = await apiClient.get<ApiWorkflowListResponse>('/workflows', {
    params,
  });
  return res.data;
}

export async function getWorkflow(id: string): Promise<ApiWorkflow> {
  const res = await apiClient.get<ApiWorkflow>(`/workflows/${id}`);
  return res.data;
}

export async function updateWorkflow(id: string, data: UpdateWorkflowBody): Promise<ApiWorkflow> {
  const res = await apiClient.put<ApiWorkflow>(`/workflows/${id}`, data);
  return res.data;
}

export async function deleteWorkflow(id: string): Promise<{ deleted: boolean }> {
  const res = await apiClient.delete<{ deleted: boolean }>(`/workflows/${id}`);
  return res.data;
}

export async function duplicateWorkflow(id: string): Promise<ApiWorkflow> {
  const res = await apiClient.post<ApiWorkflow>(`/workflows/${id}/duplicate`);
  return res.data;
}

export async function updateWorkflowStatus(id: string, status: string): Promise<ApiWorkflow> {
  const res = await apiClient.patch<ApiWorkflow>(`/workflows/${id}/status`, {
    status,
  });
  return res.data;
}

// Graph operations
export async function saveWorkflowGraph(
  id: string,
  data: SaveWorkflowGraphBody
): Promise<ApiWorkflow> {
  const res = await apiClient.put<ApiWorkflow>(`/workflows/${id}/graph`, data);
  return res.data;
}

export async function getWorkflowGraph(id: string): Promise<ApiWorkflowGraphResponse> {
  const res = await apiClient.get<ApiWorkflowGraphResponse>(`/workflows/${id}/graph`);
  return res.data;
}

export async function generateWorkflow(description: string): Promise<ApiWorkflow> {
  const res = await apiClient.post<ApiWorkflow>('/workflows/generate', { description });
  return res.data;
}

// Templates
export async function listTemplates(): Promise<ApiWorkflowTemplateMeta[]> {
  const res = await apiClient.get<ApiWorkflowTemplateMeta[]>('/workflows/templates');
  return res.data;
}

export async function createFromTemplate(templateId: string): Promise<ApiWorkflow> {
  const res = await apiClient.post<ApiWorkflow>(`/workflows/from-template/${templateId}`);
  return res.data;
}

// Testing and execution
export async function testWorkflow(
  id: string,
  triggerData?: Record<string, unknown>
): Promise<ApiWorkflowRun> {
  const res = await apiClient.post<ApiWorkflowRun>(`/workflows/${id}/test`, {
    triggerData: triggerData ?? {},
  });
  return res.data;
}

export async function executeWorkflow(
  id: string,
  triggerData?: Record<string, unknown>
): Promise<ApiWorkflowRun> {
  const res = await apiClient.post<ApiWorkflowRun>(`/workflows/${id}/execute`, {
    triggerData: triggerData ?? {},
  });
  return res.data;
}

// Run history
export async function listWorkflowRuns(
  workflowId: string,
  params?: ListWorkflowRunsParams
): Promise<ApiWorkflowRunsResponse> {
  const res = await apiClient.get<ApiWorkflowRunsResponse>(`/workflows/${workflowId}/runs`, {
    params,
  });
  return res.data;
}

export async function getWorkflowRun(runId: string): Promise<ApiWorkflowRun> {
  const res = await apiClient.get<ApiWorkflowRun>(`/workflow-runs/${runId}`);
  return res.data;
}

export async function getRunSteps(runId: string): Promise<ApiWorkflowStepLog[]> {
  const res = await apiClient.get<ApiWorkflowStepLog[]>(`/workflow-runs/${runId}/steps`);
  return res.data;
}

// Workflow-generated notifications and tasks
export async function getNotifications(params?: ListParams): Promise<ApiNotificationsResponse> {
  const res = await apiClient.get<ApiNotificationsResponse>('/workflows/notifications', {
    params,
  });
  return res.data;
}

export async function getTasks(params?: ListParams): Promise<ApiTasksResponse> {
  const res = await apiClient.get<ApiTasksResponse>('/workflows/tasks', {
    params,
  });
  return res.data;
}
